const libPictProvider = require('pict').ServiceProviderBase;

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'SessionExporter',
	Dependencies: []
};

/*
	Pict-Provider-SessionExporter
	-----------------------------
	Two outputs from AppData.Session:
	  - downloadMarkdown(pOptions): triggers a .md file download
	  - printPDF(pOptions): triggers window.print() (CSS @media print rules in views drive layout)

	Options:
	  - includeTools (default: from Layout.ShowTools)
	  - includeAttachments (default: from Layout.ShowAttachments)
	  - includeAgents (default: true)
*/

class PictProviderSessionExporter extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
	}

	downloadMarkdown(pOptions)
	{
		let tmpSession = this.fable.AppData && this.fable.AppData.Session;
		if (!tmpSession) return;
		let tmpOpts = this._resolveOptions(pOptions);
		let tmpMd = this.buildMarkdown(tmpSession, tmpOpts);
		let tmpFilename = (tmpSession.Meta.title || 'claude-session').replace(/[^A-Za-z0-9_\- ]/g, '').slice(0, 80) + '.md';
		this._download(tmpFilename, tmpMd, 'text/markdown;charset=utf-8');
	}

	printPDF(pOptions)
	{
		// Print path uses the live DOM + @media print stylesheet rules. We just need the right
		// view-state toggles set first so what's rendered matches what the user wants in the PDF.
		let tmpOpts = this._resolveOptions(pOptions);
		let tmpLayout = this.fable.AppData && this.fable.AppData.Layout;
		let tmpConvView = this.fable.PictApplication && this.fable.PictApplication.pict.views.Conversation;
		if (tmpLayout && tmpConvView)
		{
			let tmpPrevTools = tmpLayout.ShowTools;
			let tmpPrevAttach = tmpLayout.ShowAttachments;
			tmpLayout.ShowTools = !!tmpOpts.includeTools;
			tmpLayout.ShowAttachments = !!tmpOpts.includeAttachments;
			tmpConvView.render();
			// Defer print so DOM updates settle.
			setTimeout(() =>
			{
				window.print();
				tmpLayout.ShowTools = tmpPrevTools;
				tmpLayout.ShowAttachments = tmpPrevAttach;
				tmpConvView.render();
			}, 50);
		}
		else
		{
			window.print();
		}
	}

	buildMarkdown(pSession, pOpts)
	{
		let tmpOut = [];
		tmpOut.push('# ' + (pSession.Meta.title || 'Claude session'));
		tmpOut.push('');
		tmpOut.push('- Session: `' + (pSession.Meta.sessionId || '') + '`');
		if (pSession.Meta.model) tmpOut.push('- Model: `' + pSession.Meta.model + '`');
		tmpOut.push('- Started: ' + (pSession.Meta.createdAt ? new Date(pSession.Meta.createdAt).toISOString() : ''));
		tmpOut.push('- Total wall time: ' + formatDuration(pSession.Stats.totalDurationMs));
		tmpOut.push('- Active: ' + formatDuration(pSession.Stats.activeMs)
			+ ' · Idle: ' + formatDuration(pSession.Stats.idleMs));
		tmpOut.push('- ' + pSession.Stats.promptCount + ' prompts · '
			+ pSession.Stats.toolCallCount + ' tool calls · '
			+ pSession.Stats.agentCount + ' subagents');
		tmpOut.push('');

		let tmpRenderedPrompts = {};
		let tmpPromptIdx = 0;
		for (let i = 0; i < pSession.Turns.length; i++)
		{
			let tmpT = pSession.Turns[i];
			if (tmpT.Kind === 'gap')
			{
				tmpOut.push('');
				tmpOut.push('---');
				tmpOut.push('*Idle ' + formatDuration(tmpT.DurationMs) + '*');
				tmpOut.push('---');
				tmpOut.push('');
				continue;
			}
			if (tmpT.Kind === 'attachment' && !pOpts.includeAttachments) continue;
			if (tmpT.Kind === 'system' && !pOpts.includeAttachments) continue;
			if (tmpT.Kind === 'tool-result' && !pOpts.includeTools) continue;
			if (tmpT.Kind === 'assistant' && tmpT.IsIntermediary && !pOpts.includeIntermediaries) continue;

			if (tmpT.PromptId && !tmpRenderedPrompts[tmpT.PromptId])
			{
				tmpRenderedPrompts[tmpT.PromptId] = true;
				tmpPromptIdx++;
				let tmpGroup = pSession.Prompts.find((pG) => pG.PromptId === tmpT.PromptId);
				if (tmpGroup)
				{
					tmpOut.push('');
					tmpOut.push('## Prompt #' + tmpPromptIdx + ' — ' + (tmpGroup.Title || ''));
					tmpOut.push('*' + new Date(tmpGroup.FirstTimestampMs).toISOString()
						+ ' · ' + formatDuration(tmpGroup.TotalDurationMs)
						+ ' · ' + tmpGroup.ToolCallCount + ' tools'
						+ (tmpGroup.AgentCallCount ? ' · ' + tmpGroup.AgentCallCount + ' agents' : '')
						+ '*');
					tmpOut.push('');
				}
			}

			let tmpKindLabel = ({
				'user-prompt': '### User',
				'assistant':   '### Assistant',
				'tool-result': '#### Tool result',
				'attachment':  '#### Attachment',
				'system':      '#### System'
			})[tmpT.Kind] || ('#### ' + tmpT.Kind);
			tmpOut.push(tmpKindLabel + ' — `' + (tmpT.Timestamp || '') + '`');
			tmpOut.push('');

			for (let j = 0; j < tmpT.Blocks.length; j++)
			{
				let tmpB = tmpT.Blocks[j];
				if (tmpB.Type === 'text')
				{
					tmpOut.push(tmpB.Text || '');
					tmpOut.push('');
				}
				else if (tmpB.Type === 'thinking')
				{
					if (pOpts.includeAttachments)
					{
						tmpOut.push('> *thinking:* ' + (tmpB.Text || '').replace(/\n/g, '\n> '));
						tmpOut.push('');
					}
				}
				else if (tmpB.Type === 'tool_use')
				{
					if (!pOpts.includeTools) continue;
					tmpOut.push('- 🛠 **' + tmpB.Name + '**'
						+ (tmpB.InputSummary ? ' — `' + tmpB.InputSummary.replace(/`/g, '\\`') + '`' : ''));
				}
				else if (tmpB.Type === 'tool_result')
				{
					if (!pOpts.includeTools) continue;
					let tmpDur = tmpB.DurationMs != null ? ' (' + formatDuration(tmpB.DurationMs) + ')' : '';
					tmpOut.push('  - result' + tmpDur + ':');
					tmpOut.push('    ```');
					let tmpLines = (tmpB.ContentText || '').split('\n').slice(0, 40);
					for (let k = 0; k < tmpLines.length; k++) tmpOut.push('    ' + tmpLines[k]);
					if (tmpB.ContentTruncated) tmpOut.push('    [truncated]');
					tmpOut.push('    ```');
				}
				else if (tmpB.Type === 'attachment')
				{
					if (!pOpts.includeAttachments) continue;
					tmpOut.push('*[attachment: ' + tmpB.AttachmentType + ' — ' + (tmpB.Summary || '') + ']*');
				}
			}
			tmpOut.push('');
		}

		if (pOpts.includeAgents && pSession.Agents)
		{
			let tmpAgentIds = Object.keys(pSession.Agents);
			if (tmpAgentIds.length)
			{
				tmpOut.push('');
				tmpOut.push('---');
				tmpOut.push('## Subagent transcripts');
				for (let i = 0; i < tmpAgentIds.length; i++)
				{
					let tmpAg = pSession.Agents[tmpAgentIds[i]];
					tmpOut.push('');
					tmpOut.push('### ' + tmpAg.AgentType + ' (' + formatDuration(tmpAg.DurationMs) + ')');
					tmpOut.push('*' + (tmpAg.Description || '') + '*');
					for (let j = 0; j < tmpAg.Turns.length; j++)
					{
						let tmpT = tmpAg.Turns[j];
						for (let k = 0; k < tmpT.Blocks.length; k++)
						{
							let tmpB = tmpT.Blocks[k];
							if (tmpB.Type === 'text') { tmpOut.push(''); tmpOut.push(tmpB.Text || ''); }
							else if (tmpB.Type === 'tool_use') tmpOut.push('  - 🛠 ' + tmpB.Name + (tmpB.InputSummary ? ': `' + tmpB.InputSummary + '`' : ''));
						}
					}
				}
			}
		}

		return tmpOut.join('\n');
	}

	_resolveOptions(pOptions)
	{
		let tmpL = (this.fable.AppData && this.fable.AppData.Layout) || {};
		return Object.assign({
			includeTools: tmpL.ShowTools !== false,
			includeAttachments: !!tmpL.ShowAttachments,
			includeIntermediaries: tmpL.ShowIntermediaries !== false,
			includeAgents: true
		}, pOptions || {});
	}

	_download(pFilename, pContent, pMime)
	{
		let tmpBlob = new Blob([pContent], { type: pMime });
		let tmpUrl = URL.createObjectURL(tmpBlob);
		let tmpA = document.createElement('a');
		tmpA.href = tmpUrl;
		tmpA.download = pFilename;
		document.body.appendChild(tmpA);
		tmpA.click();
		setTimeout(() => { document.body.removeChild(tmpA); URL.revokeObjectURL(tmpUrl); }, 100);
	}
}

function formatDuration(pMs)
{
	if (!pMs || pMs < 0) return '0s';
	let tmpS = Math.floor(pMs / 1000);
	if (tmpS < 60) return tmpS + 's';
	let tmpM = Math.floor(tmpS / 60);
	if (tmpM < 60) return tmpM + 'm ' + (tmpS % 60) + 's';
	let tmpH = Math.floor(tmpM / 60);
	if (tmpH < 24) return tmpH + 'h ' + (tmpM % 60) + 'm';
	let tmpD = Math.floor(tmpH / 24);
	return tmpD + 'd ' + (tmpH % 24) + 'h';
}

module.exports = PictProviderSessionExporter;
module.exports.default_configuration = _DefaultProviderConfiguration;
